import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { PopupBanner } from '../../services/admin/popupApi';

const STORAGE_KEY = 'popup_dismissed_date';

function getTodayString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export function shouldShowPopup(): boolean {
  return localStorage.getItem(STORAGE_KEY) !== getTodayString();
}

interface Props {
  popups: PopupBanner[];
  onClose: () => void;
}

const LoginPopupModal: React.FC<Props> = ({ popups, onClose }) => {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = popups.length;
  const current = popups[index];

  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);

  // 팝업이 2개 이상이면 5초마다 자동 슬라이드
  useEffect(() => {
    if (total <= 1) return;
    const interval = setInterval(() => next(), 5000);
    return () => clearInterval(interval);
  }, [total, next]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta < -50) next();
    else if (delta > 50) prev();
    touchStartX.current = null;
  };

  const handleDismissToday = () => {
    localStorage.setItem(STORAGE_KEY, getTodayString());
    onClose();
  };

  const handleImageClick = () => {
    if (current.link_url) {
      window.open(current.link_url, '_blank', 'noopener,noreferrer');
    }
  };

  if (!current) return null;

  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ImageArea
          $clickable={!!current.link_url}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onClick={handleImageClick}
        >
          <PopupImage src={current.image_url} alt={`팝업 배너 ${index + 1}`} />

          {total > 1 && (
            <>
              <ArrowButton $side="left" onClick={(e) => { e.stopPropagation(); prev(); }}>
                ‹
              </ArrowButton>
              <ArrowButton $side="right" onClick={(e) => { e.stopPropagation(); next(); }}>
                ›
              </ArrowButton>
            </>
          )}
        </ImageArea>

        {total > 1 && (
          <Dots>
            {popups.map((_, i) => (
              <Dot key={i} $active={i === index} onClick={() => setIndex(i)} />
            ))}
          </Dots>
        )}

        <ActionArea>
          <ActionButton type="button" $side="left" onClick={handleDismissToday}>
            오늘 하루 열지않기
          </ActionButton>
          <ActionButton type="button" $side="right" onClick={onClose}>
            닫기
          </ActionButton>
        </ActionArea>
      </Modal>
    </Overlay>,
    document.body,
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(23, 23, 25, 0.52);
  padding: 20px;
`;

const Modal = styled.div`
  width: min(400px, calc(100vw - 40px));
  max-height: 85dvh;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ImageArea = styled.div<{ $clickable: boolean }>`
  position: relative;
  flex: 1;
  overflow: hidden;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  aspect-ratio: 720 / 1022;
  min-height: 0;
`;

const PopupImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
`;

const ArrowButton = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => $side}: 12px;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 22px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: rgba(0, 0, 0, 0.55); }
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 8px 0 4px;
  background: #fff;
`;

const Dot = styled.button<{ $active: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: pointer;
  background: ${({ $active }) => ($active ? '#448181' : 'rgba(112,115,124,0.3)')};
  transition: background 0.2s;
`;

const ActionArea = styled.div`
  display: flex;
  border-top: 1px solid rgba(112, 115, 124, 0.16);
  flex-shrink: 0;
`;

const ActionButton = styled.button<{ $side: 'left' | 'right' }>`
  flex: 1;
  padding: 14px 12px;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.467;
  color: #171719;
  background: #fff;
  border: none;
  border-radius: ${({ $side }) => ($side === 'left' ? '0 0 0 10px' : '0 0 10px 0')};
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
  &:hover { background: rgba(112, 115, 124, 0.05); }
  ${({ $side }) => $side === 'left' && `border-right: 1px solid rgba(112,115,124,0.16);`}
`;

export default LoginPopupModal;
